/**
 * Which part each uploaded file plays. People pick a folder's worth of files,
 * so the part comes from the content: an `.xml` is the events, a file that
 * declares `interface Dispatcher` or `interface Controller` is a schema root,
 * and everything else is an include. That is the bundle the converter expects.
 */
import { MceIdlSources, MceIdlUploadFile } from "./messages";

const INTERFACE_LINE = /^\s*interface\s+(Dispatcher|Controller)\s*$/m;

/** The bundle, or one plain reason there is none. */
export type SortedFiles =
  { kind: "ok"; sources: MceIdlSources } | { kind: "problem"; message: string };

/**
 * Includes go in byte order of their UTF-8 names, as the studio sends them: the
 * converter hashes them as sent, so another order would give the same files
 * another sha, and so a duplicate adapter and a missed dedup on VTS.
 */
function byNameBytes(a: MceIdlUploadFile, b: MceIdlUploadFile): number {
  const left = new TextEncoder().encode(a.name);
  const right = new TextEncoder().encode(b.name);
  const shared = Math.min(left.length, right.length);
  for (let index = 0; index < shared; index += 1) {
    if (left[index] !== right[index]) return left[index] - right[index];
  }
  return left.length - right.length;
}

function declaredInterface(file: MceIdlUploadFile): string | null {
  return new TextDecoder().decode(file.bytes).match(INTERFACE_LINE)?.[1] ?? null;
}

/** Sort picked files into the parts of a bundle. */
export default function sortMceIdlFiles(files: MceIdlUploadFile[]): SortedFiles {
  const xml = files.filter((file) => file.name.toLowerCase().endsWith(".xml"));
  if (xml.length > 1) return { kind: "problem", message: "Choose only one events XML file." };
  const idl = files.filter((file) => !xml.includes(file));
  const withInterface = idl.map((file) => ({ file, iface: declaredInterface(file) }));
  const dispatchers = withInterface.filter((f) => f.iface === "Dispatcher");
  const controllers = withInterface.filter((f) => f.iface === "Controller");
  if (dispatchers.length > 1 || controllers.length > 1) {
    return { kind: "problem", message: "Choose the files of one IDL at a time." };
  }
  const main = dispatchers[0]?.file ?? controllers[0]?.file;
  if (!main) {
    return {
      kind: "problem",
      message: "None of these files declares an MCE interface. Add the main IDL file.",
    };
  }
  const controller = dispatchers.length > 0 ? controllers[0]?.file : undefined;
  return {
    kind: "ok",
    sources: {
      main,
      controller,
      includes: idl.filter((file) => file !== main && file !== controller).sort(byNameBytes),
      eventsXml: xml[0],
    },
  };
}
